import { describeApiError, type ExportChoice, type ExportTarget } from '@brush/api-client';
import { ErrorNotice, SegmentedControl, smallButtonClass } from '@brush/ui';
import { Download, Printer } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  EXPORT_FORMATS,
  exportNote,
  exportOffered,
  exportRefusal,
  fallbackFileName,
  freshChoice,
  printLine,
  type ExportAvailability,
  type PrintPage,
} from '../export/exportControl';
import { useExportDownload } from '../queries';
import { useRoom } from '../room';

/**
 * A view's *Export* control (docs/specs/export.md §6): a format, *Include card IDs*, *Download* and *Print*, beside the
 * view's heading — and, printed above the table, the line naming the room, the view and the moment.
 *
 * - **Card IDs start unticked every time the panel opens** (EX15, `freshChoice`): the choice is never remembered,
 *   not across opens and not across views. The format is kept for as long as the control is mounted.
 * - **Shown only where the export can answer** (EX30, `exportOffered`): nothing on a Draft board, a platform template
 *   or a contest that has not locked.
 * - **The download is fetch → Blob → object URL** (EX25) through `useExportDownload`; the file is named by the server.
 *   While it builds, Download says so and cannot be pressed twice — a second press would be a second export and a
 *   second audit record.
 * - **A refusal is shown beside the control**, in the export's own words where it has them (`exportRefusal`) and
 *   `describeApiError`'s otherwise.
 * - **Chalk, never signal orange**: an export is not work waiting on the operator.
 * - **The control itself never prints** (`print:hidden`); the print line prints and shows nowhere else.
 */
export function ExportControl({
  availability,
  target,
  viewLabel,
  subject,
  page,
}: {
  availability: ExportAvailability;
  /** Null while the view has not resolved what it would export. */
  target: ExportTarget | null;
  /** What the print line calls the view ("Tournaments", or the board's name). */
  viewLabel: string;
  /** The fallback file name's subject, used only if the response names no file. */
  subject: string;
  /** T1 only: the page on screen, which is what Print prints (Answered 13a). */
  page?: PrintPage;
}) {
  const { room } = useRoom();
  const download = useExportDownload();
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState<ExportChoice>(() => freshChoice());
  const [printedAt, setPrintedAt] = useState(() => new Date());
  const panelRef = useRef<HTMLDivElement>(null);

  // The moment printed is the moment of printing, not of the last render.
  useEffect(() => {
    const stamp = () => setPrintedAt(new Date());
    window.addEventListener('beforeprint', stamp);
    return () => window.removeEventListener('beforeprint', stamp);
  }, []);

  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!exportOffered(availability) || !room) return null;

  const toggle = () => {
    if (!open) {
      // EX15: unticked every time the panel opens.
      setChoice((last) => freshChoice(last));
      download.reset();
    }
    setOpen((v) => !v);
  };

  const run = () => {
    if (!target || download.isPending) return;
    download.mutate({
      target,
      choice,
      fallbackName: fallbackFileName([room.name, viewLabel, subject], choice.format),
    });
  };

  const refusal = download.error ? (exportRefusal(availability.view, download.error) ?? describeApiError(download.error)) : null;

  return (
    <>
      <div className="relative print:hidden">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-haspopup="dialog"
          className={`${smallButtonClass} inline-flex items-center gap-1.5`}
        >
          <Download className="size-3.5" aria-hidden /> Export
        </button>

        {open ? (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden />
            <div
              ref={panelRef}
              tabIndex={-1}
              role="dialog"
              aria-label="Export"
              className="absolute right-0 top-full z-40 mt-2 w-[min(20rem,calc(100vw-2rem))] border border-line bg-surface p-4 text-left shadow-lg outline-none"
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-ink-800/50">Format</p>
              <div className="mt-1.5">
                <SegmentedControl
                  ariaLabel="Format"
                  options={EXPORT_FORMATS}
                  value={choice.format}
                  onChange={(format) => setChoice((c) => ({ ...c, format }))}
                />
              </div>

              <label className="mt-3 flex items-start gap-2 text-[12.5px] text-ink-900">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={choice.cardIds}
                  onChange={(e) => setChoice((c) => ({ ...c, cardIds: e.target.checked }))}
                />
                <span>
                  Include card IDs
                  <span className="block text-[11px] text-ink-800/55">
                    Off unless ticked, every time. Without them the file names players by name, city and state.
                  </span>
                </span>
              </label>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={run}
                  disabled={!target || download.isPending}
                  aria-busy={download.isPending}
                  className={`${smallButtonClass} inline-flex items-center gap-1.5`}
                >
                  <Download className="size-3.5" aria-hidden />
                  {download.isPending ? 'Preparing file…' : 'Download'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    // After the panel has closed, so it is not what the print preview opens on.
                    window.setTimeout(() => window.print(), 0);
                  }}
                  className={`${smallButtonClass} inline-flex items-center gap-1.5`}
                >
                  <Printer className="size-3.5" aria-hidden /> Print
                </button>
              </div>

              {exportNote(availability) ? (
                <p className="mt-2 text-[11px] leading-relaxed text-ink-800/60">{exportNote(availability)}</p>
              ) : null}
              {page ? (
                <p className="mt-2 text-[11px] leading-relaxed text-ink-800/60">
                  Print shows this page ({page.page} of {page.pageCount}). Download has every tournament the filter
                  matches.
                </p>
              ) : null}

              {download.isSuccess ? (
                <p className="mt-2 text-[11px] text-ink-800/60">Saved {download.data}.</p>
              ) : null}
              <ErrorNotice message={refusal} className="mt-3" />
            </div>
          </>
        ) : null}
      </div>

      <p className="hidden text-[11px] text-ink-800 print:mb-2 print:block">
        {printLine({ roomName: room.name, viewLabel, printedAt, timeZone: room.timeZone, page })}
      </p>
    </>
  );
}
